import axios from "axios";

// ==================== CONFIGURATION ====================
// Endpoint d'interprétation AI pour la page Adaptation Options.
// Utilise le proxy OpenAI du service distant (https://ai-interpreter.ssa.tg/api/openai/interpret),
// qui masque la clé OpenAI côté client et n'exige que cette clé d'accès au service.
const AI_INTERPRET_URL =
  import.meta.env?.VITE_ADAPTATION_AI_URL ||
  "https://ai-interpreter.ssa.tg/api/openai/interpret";
const AI_INTERPRET_KEY = import.meta.env?.VITE_ADAPTATION_AI_KEY || "";

// ==================== TYPES ====================
export interface AdaptationInterpretationResponse {
  data: string; // Texte brut généré par le modèle (JSON attendu côté page)
}

// ==================== CACHE ====================
// Cache en mémoire pour éviter de ré-appeler l'API avec un contexte identique.
const CACHE_MAX_ENTRIES = 100;
const cache = new Map<string, AdaptationInterpretationResponse>();
// Déduplication : évite de lancer plusieurs requêtes identiques en parallèle.
const inFlight = new Map<string, Promise<AdaptationInterpretationResponse>>();

/**
 * Vide le cache d'interprétation (utile après un changement de clé ou de version).
 */
export const clearAdaptationInterpretationCache = () => {
  cache.clear();
};

/**
 * Envoie un contexte d'interprétation climatique au service distant
 * et retourne le texte généré par le modèle.
 *
 * Contrat : on envoie `{ context, model }` en JSON et on reçoit `{ data: <texte> }`.
 * Selon la spec de l'endpoint, `context` peut être une chaîne ou tout objet/tableau
 * (sérialisé en JSON s'il n'est pas déjà une chaîne).
 *
 * Le résultat est mis en cache : un même `context` ne déclenche qu'un seul appel.
 */
export const fetchAdaptationInterpretation = async (
  context: string | Record<string, unknown> | unknown[],
  model = "gpt-4o-mini",
): Promise<AdaptationInterpretationResponse> => {
  // Sérialisation : si context n'est pas une chaîne, on le convertit en JSON.
  const payload =
    typeof context === "string" ? context : JSON.stringify(context);
  const cacheKey = `${model}::${payload}`;

  // 1. Résultat déjà en cache
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  // 2. Requête identique déjà en cours
  const pending = inFlight.get(cacheKey);
  if (pending) return pending;

  // 3. Nouvelle requête
  const request = (async () => {
    const { data } = await axios.post(
      AI_INTERPRET_URL,
      { context: payload, model },
      {
        headers: AI_INTERPRET_KEY
          ? {
              Authorization: `Bearer ${AI_INTERPRET_KEY}`,
              "X-API-Key": AI_INTERPRET_KEY,
            }
          : undefined,
      },
    );

    // La réponse peut être `{ data: "<texte>" }` ou directement le texte.
    const text = typeof data?.data === "string" ? data.data : String(data);
    const result: AdaptationInterpretationResponse = { data: text };

    // Insertion en cache avec limite (LRU simple : suppression de la plus ancienne).
    if (cache.size >= CACHE_MAX_ENTRIES) {
      const oldestKey = cache.keys().next().value;
      if (oldestKey !== undefined) cache.delete(oldestKey);
    }
    cache.set(cacheKey, result);
    return result;
  })();

  inFlight.set(cacheKey, request);
  try {
    return await request;
  } finally {
    inFlight.delete(cacheKey);
  }
};
